import React from 'react';
import update from 'immutability-helper';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { DropTarget } from 'react-dnd';
import { forEach as _forEach, findIndex as _findIndex } from 'lodash';

import { getNameInCurrentLanguage } from 'helpers/intl';

import EntityPropsUpdater from 'component/EntityPropsUpdater';
import List from 'components/List';

import FormAutocomplete from 'components/forms/FormAutocomplete';

import Loader from 'components/layout/Loader';
import CategoryAttributeGroup from 'components/layout/item/CategoryAttributeGroup';
import CategoryAttributeGroupAttribute from 'components/layout/item/CategoryAttributeGroupAttribute';

const target = {
  hover(props, monitor, component) {
    const tolerance = 100;
    const { y: mouseY } = monitor.getClientOffset();

    if (mouseY < 60 + tolerance) {
      // 60 for the menu
      // move up
      window.scrollBy(0, -1);
    } else if (props.layout.height - mouseY < tolerance) {
      // move down
      window.scrollBy(0, 1);
    }
  },
};

@DropTarget(['attribute', 'attributeGroup'], target, connect => ({
  connectDropTarget: connect.dropTarget(),
}))
class CategoryAttributeGroups extends EntityPropsUpdater {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      hasUnassignedAttributes: true,
    };
  }

  // when the attributes list loads, check if there are any unassigned attributes
  componentDidUpdate = prevProps => {
    if (prevProps.attributes.pending && !this.props.attributes.pending) {
      const unassignedAttributes = this.getUnassignedAttributes();
      this.setState({ hasUnassignedAttributes: !!unassignedAttributes.length });
    }
  };

  // add attribute group at the end of the list
  addAttributeGroup = attributeGroup => {
    const { entity, changeEntity } = this.props;

    if (_findIndex(entity.getAttributeGroups(), ['id', attributeGroup.id]) > -1) {
      return;
    }

    let newValue = entity.getAttributeGroups().slice();

    const value = entity.createValue('attributeGroups', [], {
      id: attributeGroup.id,
      attribute_group: attributeGroup, // we need to remember about underscore names when creating values from entity
    });
    newValue.push(value);

    let newEntity = { ...entity.updateValue('attributeGroups', newValue, []) };

    changeEntity(newEntity);
  };

  // when dragging, move the attribute groups
  moveAttributeGroups = (fromIndex, toIndex) => {
    const { entity, changeEntity } = this.props;

    const myData = { attributeGroups: entity.getAttributeGroups().slice() };
    const attributeGroup = myData.attributeGroups[fromIndex];
    const newData = update(myData, {
      attributeGroups: {
        $splice: [[fromIndex, 1], [toIndex, 0, attributeGroup]],
      },
    });

    let newEntity = { ...entity.updateValue('attributeGroups', newData.attributeGroups, []) };

    changeEntity(newEntity);
  };

  // accept an attribute to an empty group
  moveAttribute = (index, groupFrom, groupTo, dropIndex = 1) => {
    const { entity, changeEntity } = this.props;
    //console.log(index, groupFrom, groupTo, dropIndex)

    let attributeIndexCounter = this.getUnassignedAttributes().length,
      fromIndex,
      toIndex,
      fromIndexStart,
      toIndexStart;

    for (let i = 0; i < entity.getAttributeGroups().length; i++) {
      const attributeGroup = entity.getAttributeGroups()[i];
      if (attributeGroup.getId() === groupFrom) {
        fromIndexStart = attributeIndexCounter;
        fromIndex = i;
      }

      if (attributeGroup.getId() === groupTo) {
        toIndexStart = attributeIndexCounter;
        toIndex = i;
      }

      attributeIndexCounter += attributeGroup.getAttributes().length;
    }

    //console.log(fromIndex, toIndex, fromIndexStart, toIndexStart)

    if (groupFrom === null) {
      const attribute = this.getUnassignedAttributes()[index];
      const attributeValue = entity.createValue('attributes', ['attributeGroups', toIndex.toString()], {
        id: attribute.id,
        attribute,
      });

      let newAttributes = entity
        .getAttributeGroups()
        [toIndex].getAttributes()
        .slice();
      newAttributes.splice(dropIndex - toIndexStart, 0, attributeValue);

      let newEntity = { ...entity.updateValue('attributes', newAttributes, ['attributeGroups', toIndex.toString()]) };

      changeEntity(newEntity);
    } else if (groupFrom === groupTo) {
      const myData = {
        attributes: entity
          .getAttributeGroups()
          [toIndex].getAttributes()
          .slice(),
      };
      const attribute = myData.attributes[index - fromIndexStart];
      const newData = update(myData, {
        attributes: {
          $splice: [[index - fromIndexStart, 1], [dropIndex - toIndexStart, 0, attribute]],
        },
      });

      let newEntity = {
        ...entity.updateValue('attributes', newData.attributes, ['attributeGroups', toIndex.toString()]),
      };

      changeEntity(newEntity);
    } else {
      // get the attribute
      const attribute = entity.getAttributeGroups()[fromIndex].getAttributes()[index - fromIndexStart];

      // remove the attribute from the old group
      let groupFromAttributes = entity
        .getAttributeGroups()
        [fromIndex].getAttributes()
        .slice();
      groupFromAttributes.splice(index - fromIndexStart, 1);

      // add the attribute to the new group
      let groupToAttributes = entity
        .getAttributeGroups()
        [toIndex].getAttributes()
        .slice();
      groupToAttributes.splice(dropIndex - toIndexStart, 0, attribute);

      // update entity (both groups)
      let newEntity = {
        ...entity.updateValue('attributes', groupFromAttributes, ['attributeGroups', fromIndex.toString()]),
      };
      newEntity = {
        ...newEntity.updateValue('attributes', groupToAttributes, ['attributeGroups', toIndex.toString()]),
      };

      changeEntity(newEntity);
    }
  };

  // check which attributes are not assigned to a group
  getUnassignedAttributes = () => {
    const { entity, attributes } = this.props;
    let res = [];

    _forEach(attributes.data.list, (attribute, i) => {
      let found = false;

      for (let j = 0; j < entity.getAttributeGroups().length; j++) {
        const attributeGroup = entity.getAttributeGroups()[j];
        if (_findIndex(attributeGroup.getAttributes(), ['id', attribute.id]) > -1) {
          found = true;
          break;
        }
      }

      if (!found) {
        res.push(attribute);
      }
    });

    return res;
  };

  removeAttribute = (attribute, groupId) => {
    const { entity, changeEntity } = this.props;

    let index = _findIndex(entity.getAttributeGroups(), ['id', groupId]);
    let groupAttributes = entity
      .getAttributeGroups()
      [index].getAttributes()
      .slice();
    groupAttributes.splice(_findIndex(groupAttributes, ['id', attribute.id]), 1);

    // update entity
    let newEntity = { ...entity.updateValue('attributes', groupAttributes, ['attributeGroups', index.toString()]) };
    changeEntity(newEntity);
  };

  removeGroup = groupId => {
    const { entity, changeEntity } = this.props;

    let index = _findIndex(entity.getAttributeGroups(), ['id', groupId]);
    let groups = entity.getAttributeGroups();
    groups.splice(index, 1);

    // update entity
    let newEntity = { ...entity.updateValue('attributeGroups', groups, []) };
    changeEntity(newEntity);
  };

  render() {
    const { appIntl, entity, entityLoaded, attributes, intl, connectDropTarget } = this.props;
    const { hasUnassignedAttributes } = this.state;
    const messages = intl.messages;

    // indexing the attribute so that every draggable attribute has a unique one
    let attributeIndexCounter = 0;

    return connectDropTarget(
      <div class="clearfix">
        {entityLoaded && (
          <List
            noRender
            listName="categoryAttributes"
            url="/api/attributes.json"
            urlData={{ filters: { categories: entity.getId() } }}
            list={attributes}
            resPerPage={200}
          />
        )}

        {entityLoaded && !attributes.pending ? (
          <Row>
            {hasUnassignedAttributes && (
              <Col xs={12} md={4}>
                <div class="panel panel-transparent">
                  <div class="panel-body">
                    <p>{messages.formGroup.basic_information}</p>

                    <div class="">
                      {this.getUnassignedAttributes().map((attribute, i) => {
                        return (
                          <CategoryAttributeGroupAttribute
                            name={getNameInCurrentLanguage(attribute.name, appIntl)}
                            moveAttribute={this.moveAttribute}
                            messages={messages}
                            index={attributeIndexCounter++}
                            key={i}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Col>
            )}

            <Col xs={12} md={hasUnassignedAttributes ? 8 : 12}>
              <div class="panel panel-transparent">
                <div class="panel-body">
                  {entity.getAttributeGroups().map((attributeGroup, i) => {
                    attributeIndexCounter += attributeGroup.getAttributes().length;

                    return (
                      <CategoryAttributeGroup
                        data={attributeGroup}
                        attributeIndexCounter={attributeIndexCounter - attributeGroup.getAttributes().length}
                        moveAttributeGroups={this.moveAttributeGroups}
                        moveAttribute={this.moveAttribute}
                        removeAttribute={this.removeAttribute}
                        removeGroup={this.removeGroup}
                        appIntl={appIntl}
                        messages={messages}
                        index={i}
                        key={i}
                      />
                    );
                  })}

                  <FormAutocomplete
                    label="Add new attribute group"
                    name=""
                    autocompleteType="attributeGroup"
                    onChange={(name, attributeGroup) => this.addAttributeGroup(attributeGroup)}
                    returnValueObject
                  />
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <Loader />
        )}
      </div>
    );
  }
}
CategoryAttributeGroups.defaultProps = {
  inModal: false,
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    layout: state.layout,
    attributes: state.list.categoryAttributes,
  };
};

export default injectIntl(connect(mapStateToProps)(CategoryAttributeGroups));
